import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface HeaderProps {
  toggleSidebar: () => void;
}

interface Quote {
  content: string;
  author: string;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const { user, signOut, userRole } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(true);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await fetch(
          "https://api.quotable.io/random?tags=change|character|courage|friendship|generosity|gratitude|happiness|humor|imagination|knowledge|mindfulness|nature|perseverance|wisdom&maxLength=100"
        );
        if (!response.ok) throw new Error("Failed to fetch quote");
        const data = await response.json();
        setQuote({ content: data.content, author: data.author });
      } catch (error) {
        console.error("Error fetching quote:", error);
        setQuote({
          content: "Be present in all things and thankful for all things.",
          author: "Maya Angelou",
        });
      } finally {
        setIsLoadingQuote(false);
      }
    };

    fetchQuote();
  }, []);

  return (
    <header className="w-full bg-white border-b border-neutral-200 shadow-sm">
      <div className="py-1 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center flex-1">
            <button
              onClick={toggleSidebar}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <Menu size={24} />
            </button>

            <div className="flex items-center">
              <div className="ml-3 relative">
                <div>
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                      {user?.email?.charAt(0).toUpperCase()}
                    </div>
                  </button>
                </div>

                {showProfileMenu && (
                  <div className="origin-top-right absolute left-0 mt-2 rounded-md shadow-lg py-1 bg-secondary ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="block px-4 py-2 text-sm text-white border-b border-neutral-200">
                      <div className="font-medium">{user?.email}</div>
                      <div className="text-xs text-white capitalize">
                        {userRole || "Staff"}
                      </div>
                    </div>
                    <Link
                      to="/change-password"
                      className="block px-4 py-2 text-sm text-white hover:bg-neutral-100 hover:text-neutral-900"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      Change Password
                    </Link>
                    <button
                      onClick={async () => {
                        console.log("Sign out button clicked");
                        setShowProfileMenu(false);
                        try {
                          await signOut();
                          console.log("Sign out successful");
                        } catch (error: any) {
                          console.error("Sign out error:", error);
                          // Even if there's an error, still navigate to login
                          // because the session might already be invalid
                        }
                        // Always navigate to login regardless of signOut result
                        navigate("/login");
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-neutral-100 hover:text-neutral-900"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex md:flex-1 md:items-center">
              {isLoadingQuote ? (
                <div className="animate-pulse h-4 w-96 bg-neutral-200 rounded"></div>
              ) : (
                quote && (
                  <div className="px-4 w-full text-right">
                    <p className="text-neutral-600 text-sm italic line-clamp-2">
                      {quote.content}
                    </p>
                    <p className="text-neutral-500 text-sm">— {quote.author}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
